import type { EndorsementComponentData } from "@tm/api-utils";
import { navigationActions } from "@tm/insurance/navigation";
import { useBreakpointV2 } from "@tm/ui-widgets";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorScreen } from "../../../ui-container/ErrorScreen";
import WebToast from "../../components/Toast/WebToast";
import { ORDER_DETAIL_V2_ASSETS } from "../OrderDetailV2/constants";
import { CancelPolicyProvider } from "./CancelPolicyContext";
import { CancelPolicyResultModal } from "./components/CancelPolicyResultModal";
import { useCancellationData } from "./hooks/useCancellationData";
import { useSubmitCancelPolicy } from "./hooks/useSubmitCancelPolicy";
import type { CancelPolicyWrapperProps } from "./types";
import { buildCancelPolicySubmitPayload } from "./utils/buildCancelPolicySubmitPayload";
import { buildOrderDetailsNavigationParams } from "./utils/buildOrderDetailsNavigationParams";

const CancelPolicyDesktop = dynamic(() => import("./CancelPolicyDesktop"));
const CancelPolicyPWA = dynamic(() => import("./CancelPolicyPWA"));

function CancelPolicyWrapper({ data, showNav }: CancelPolicyWrapperProps) {
  const router = useRouter();
  const device = useBreakpointV2();
  // Stabilise router.query — it returns a new object reference on every render.
  // Keying on router.asPath (a primitive string) ensures this only changes on real navigation.
  const stableQuery = useMemo(() => router.query, [router.asPath]);
  const { normalizedData, setCancellationData, refreshCancellationDetails } =
    useCancellationData({ data, routerQuery: stableQuery });
  const stepIds = useMemo(
    () => normalizedData.pageData?.stepInfo?.stepsMap?.filter(Boolean) ?? [],
    [normalizedData.pageData?.stepInfo?.stepsMap]
  );
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [stepStateById, setStepStateById] = useState<
    Record<string, { isValid: boolean; value?: Record<string, unknown> }>
  >({});
  const [isSubmitConfirmationVisible, setIsSubmitConfirmationVisible] =
    useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [resultData, setResultData] = useState<EndorsementComponentData | null>(
    null
  );
  const [resultView, setResultView] = useState<"success" | "error">("success");
  const [isResultVisible, setIsResultVisible] = useState(false);
  const { submit: submitCancelPolicy, isLoading: isSubmitting } =
    useSubmitCancelPolicy();

  useEffect(() => {
    if (!submitError) return;
    const timer = setTimeout(() => setSubmitError(null), 5000);
    return () => clearTimeout(timer);
  }, [submitError]);

  const isStepFlow = stepIds.length > 0;
  const activeStepId = stepIds[activeStepIndex];
  const totalSteps = stepIds.length;
  const isLastStep = totalSteps > 0 && activeStepIndex === totalSteps - 1;
  const activeStepState = activeStepId
    ? stepStateById[activeStepId]
    : undefined;
  const stepLabelTemplate = normalizedData.pageData?.stepInfo?.stepTitle;
  const submitConfirmationPopup =
    normalizedData.pageData?.footer?.submitConfirmationPopup;

  useEffect(() => {
    setActiveStepIndex((prevIndex) => {
      if (totalSteps <= 0) {
        return 0;
      }

      return Math.min(prevIndex, totalSteps - 1);
    });
  }, [totalSteps]);

  const stepLabel = useMemo(() => {
    if (typeof stepLabelTemplate !== "string" || !stepLabelTemplate.trim()) {
      return "";
    }

    return stepLabelTemplate
      .replaceAll("__stepNumber__", String(activeStepIndex + 1))
      .replaceAll("__totalSteps__", String(totalSteps))
      .replace(/\s+/g, " ")
      .trim();
  }, [activeStepIndex, stepLabelTemplate, totalSteps]);

  const onStepStateChange = useCallback(
    (state: { isValid: boolean; value?: Record<string, unknown> }) => {
      if (!activeStepId) {
        return;
      }

      setStepStateById((prevState) => ({
        ...prevState,
        [activeStepId]: state,
      }));
    },
    [activeStepId]
  );

  const handleSubmitCancellation = useCallback(async () => {
    const payload = buildCancelPolicySubmitPayload({
      bookingId: normalizedData.bookingId,
      stepStateById,
    });

    const query = buildOrderDetailsNavigationParams(
      normalizedData.bookingId,
      stableQuery
    );

    // Keep the confirmation popup open while the API call is in flight so the
    // primary CTA can render its loading state. Close it only once the call
    // settles (after success or on error).
    try {
      const response = await submitCancelPolicy({ payload, query });
      setIsSubmitConfirmationVisible(false);
      if (device === "lg") {
        await refreshCancellationDetails();
      } else {
        const componentData = response?.data?.components?.[0]?.data ?? null;
        setResultData(componentData as EndorsementComponentData | null);
        setResultView(response?.success === false ? "error" : "success");
        setIsResultVisible(true);
      }
    } catch (err) {
      setIsSubmitConfirmationVisible(false);
      const message =
        err instanceof Error
          ? err.message
          : "Failed to cancel policy. Please try again.";
      setSubmitError(message);
    }
  }, [
    device,
    normalizedData.bookingId,
    refreshCancellationDetails,
    stableQuery,
    stepStateById,
    submitCancelPolicy,
  ]);

  const goToNextStep = useCallback(() => {
    if (!activeStepState?.isValid || isSubmitting) {
      return;
    }

    if (isLastStep) {
      if (submitConfirmationPopup) {
        setIsSubmitConfirmationVisible(true);
        return;
      }

      handleSubmitCancellation();
      return;
    }

    setActiveStepIndex((prevIndex) => Math.min(prevIndex + 1, totalSteps - 1));
  }, [
    activeStepState?.isValid,
    handleSubmitCancellation,
    isLastStep,
    isSubmitting,
    submitConfirmationPopup,
    totalSteps,
  ]);

  const goToPreviousStep = useCallback(() => {
    setActiveStepIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  }, []);

  const closeSubmitConfirmation = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitConfirmationVisible(false);
  }, [isSubmitting]);

  const handleResultClose = useCallback(() => {
    const query = buildOrderDetailsNavigationParams(
      normalizedData.bookingId,
      stableQuery
    );
    // Keep the overlay mounted while navigation is in flight. Hiding it here
    // would briefly reveal the underlying cancel page before the route change
    // commits, causing a visible flash.
    navigationActions.navigateToOrderDetailsV2(query);
  }, [normalizedData.bookingId, stableQuery]);

  // Stable context — only recreates when booking/page identity changes.
  // Components subscribed via useCancelPolicyStatic() are immune to step-interaction re-renders.
  const staticCtxValue = useMemo(
    () => ({
      ...normalizedData,
      showNav,
      query: stableQuery,
      refreshCancellationDetails,
      setCancellationData,
    }),
    [
      normalizedData,
      showNav,
      stableQuery,
      refreshCancellationDetails,
      setCancellationData,
    ]
  );

  // Volatile context — recreates on every step interaction (typing, navigation, submit).
  const stepCtxValue = useMemo(
    () => ({
      isStepFlow,
      stepIds,
      activeStepIndex,
      activeStepId,
      totalSteps,
      stepLabel,
      isLastStep,
      stepStateById,
      onStepStateChange,
      goToNextStep,
      goToPreviousStep,
      isPrimaryDisabled: !activeStepState?.isValid || isSubmitting,
      isSubmitConfirmationVisible,
      closeSubmitConfirmation,
      confirmSubmitCancellation: handleSubmitCancellation,
      isSubmitting,
    }),
    [
      activeStepId,
      activeStepIndex,
      activeStepState?.isValid,
      closeSubmitConfirmation,
      handleSubmitCancellation,
      goToNextStep,
      goToPreviousStep,
      isLastStep,
      isSubmitConfirmationVisible,
      isSubmitting,
      isStepFlow,
      onStepStateChange,
      stepIds,
      stepLabel,
      stepStateById,
      totalSteps,
    ]
  );

  const hasContent = normalizedData.components.length > 0;

  if (!hasContent) {
    return (
      <ErrorScreen
        pageName="ORDER_DETAILS"
        trackError
        error={{
          code: "CANCEL_POLICY_EMPTY",
          message: {
            title: "Unable to load cancellation details",
            description:
              "We couldn't retrieve your cancellation information. Please try again later.",
            icon: ORDER_DETAIL_V2_ASSETS.ERROR_IMG,
          },
        }}
      />
    );
  }

  return (
    <CancelPolicyProvider staticValue={staticCtxValue} stepValue={stepCtxValue}>
      {device === "lg" ? (
        <CancelPolicyDesktop normalizedData={normalizedData} />
      ) : (
        <CancelPolicyPWA normalizedData={normalizedData} />
      )}

      {device !== "lg" && (
        <CancelPolicyResultModal
          visible={isResultVisible}
          viewType={resultView}
          data={resultData}
          onClose={handleResultClose}
        />
      )}

      {submitError && (
        <WebToast
          type="error"
          toastTxt={submitError}
          position="fixed"
          bottom="24px"
          right="24px"
          width="360px"
        />
      )}
    </CancelPolicyProvider>
  );
}

export default CancelPolicyWrapper;
